import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * AdminButtonGroup — Round 92 (Feb 2026).
 *
 * Collapsible visual grouping for the admin button row. Renders a
 * single labelled "section" pill (e.g. "🌅 Daily Refresh · 3 tasks ·
 * 24h cron") that, when clicked, expands inline to show its child
 * admin buttons. Preference is remembered per-group in localStorage.
 *
 * Props:
 *   - icon         : single emoji or short prefix (e.g. "🌅")
 *   - label        : human label (e.g. "Daily Refresh")
 *   - cadence      : short cadence tag (e.g. "24h cron", "7-day", "on-demand")
 *   - tone         : "amber" | "violet" | "emerald" | "sky" | "rose"
 *   - testid       : data-testid for the pill
 *   - storageKey   : localStorage key for open/closed memory
 *   - children     : the actual admin buttons to render inside
 *
 * The wrapper is INTENTIONALLY thin — child buttons keep all of their
 * existing state, cooldowns, modals, toast handlers. We only handle
 * the show/hide chrome around them.
 */
const TONE_MAP = {
  amber:   { pill: 'border-amber-400/50 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-700/40',
             open: 'bg-amber-100 dark:bg-amber-900/60' },
  violet:  { pill: 'border-violet-400/50 bg-violet-50 text-violet-900 dark:bg-violet-950/40 dark:text-violet-200 dark:border-violet-700/40',
             open: 'bg-violet-100 dark:bg-violet-900/60' },
  emerald: { pill: 'border-emerald-400/50 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-700/40',
             open: 'bg-emerald-100 dark:bg-emerald-900/60' },
  sky:     { pill: 'border-sky-400/50 bg-sky-50 text-sky-900 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-700/40',
             open: 'bg-sky-100 dark:bg-sky-900/60' },
  rose:    { pill: 'border-rose-400/50 bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-200 dark:border-rose-700/40',
             open: 'bg-rose-100 dark:bg-rose-900/60' },
};

export default function AdminButtonGroup({
  icon,
  label,
  cadence,
  tone = 'violet',
  testid,
  storageKey,
  children,
  defaultOpen = false,
  // Round P108av+5 — optional right-side status pill (e.g. "✓ synced 2h
  // ago" for the Streaks group). Rendered before the chevron. Pass any
  // ReactNode — component doesn't know or care what's inside.
  statusBadge = null,
}) {
  const [open, setOpen] = useState(() => {
    if (!storageKey) return defaultOpen;
    try { return localStorage.getItem(storageKey) === '1'; } catch { return defaultOpen; }
  });
  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, open ? '1' : '0'); } catch { /* swallow */ }
  }, [open, storageKey]);

  const toneStyles = TONE_MAP[tone] || TONE_MAP.violet;
  const childCount = (Array.isArray(children) ? children.filter(Boolean).length : children ? 1 : 0);

  return (
    <div className="inline-flex flex-col items-end" data-testid={`${testid}-wrap`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold uppercase tracking-wider transition-colors ${toneStyles.pill} ${open ? toneStyles.open : ''}`}
        data-testid={testid}
        title={open ? `Hide ${label}` : `Show ${label}`}
      >
        {icon && <span aria-hidden className="text-[13px] leading-none">{icon}</span>}
        <span>{label}</span>
        <span className="opacity-70 normal-case font-mono text-[10px]" data-testid={`${testid}-count`}>
          {childCount} {childCount === 1 ? 'task' : 'tasks'}
        </span>
        {cadence && (
          <span className="opacity-70 normal-case font-mono text-[10px] border-l border-current/30 pl-1.5 ml-0.5">
            {cadence}
          </span>
        )}
        {statusBadge && (
          <span className="normal-case font-normal border-l border-current/30 pl-1.5 ml-0.5" data-testid={`${testid}-status`}>
            {statusBadge}
          </span>
        )}
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div
          className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 max-w-full justify-items-stretch"
          data-testid={`${testid}-content`}
        >
          {children}
        </div>
      )}
    </div>
  );
}
